import { MutationTree, ActionTree, GetterTree } from "vuex";
import {
  ATMPair,
  ATMAsset,
  FiatAsset,
  C2COffer
} from "@foxone/api/src/models/market";
import { USDT_ASSET_ID } from "trade/constants";
import utils from "trade/utils";

const state = () => ({
  pairs: [],
  fiatPairs: [],
  instant: {
    wantedAsset: "",
    offerAsset: ""
  },
  auto: {
    wantedAsset: "",
    offerAsset: "",
    side: ""
  },
  offers: [],
  thumbnails: {},
  loading: false,
  showFiatTradeStatementModal: true
});

export type TradeState = {
  pairs: ATMPair[];
  fiatPairs: FiatAsset[];
  loading: boolean;
  tradeTabIndex: string;
  instant: {
    wantedAsset: "";
    offerAsset: "";
  };
  auto: {
    wantedAsset: ATMAsset | "";
    offerAsset: ATMAsset | "";
    side: "";
  };
  offers: C2COffer[];
  thumbnails: Map<string, string[]>;
  showFiatTradeStatementModal: boolean;
};

const getters: GetterTree<TradeState, {}> = {
  getAssets(state) {
    const pairs = state.pairs;
    const assets = {};
    pairs.forEach(pair => {
      const { base, quote } = pair;
      assets[base.asset_id] = base;
      assets[quote.asset_id] = quote;
    });
    return Object.keys(assets).map(k => assets[k]);
  },
  getFiatAssets(state) {
    const pairs = state.pairs.filter(
      pair => pair.quote.asset_id === USDT_ASSET_ID && pair.can_buy
    );
    const assets = {};
    pairs.forEach(pair => {
      const { base, quote } = pair;
      assets[base.asset_id] = base;
      assets[quote.asset_id] = quote;
    });
    return Object.keys(assets).map(k => assets[k]);
  },
  getPairsByStrategy(state) {
    return strategy => {
      return state.pairs.filter(pair => pair.strategies.includes(strategy));
    };
  },
  getThumbnailById(state) {
    return (id: string) => {
      return state.thumbnails[id] || [];
    };
  }
};

const mutations: MutationTree<TradeState> = {
  SET_PAIRS(state, data) {
    state.pairs = data;
  },
  SET_FIAT_PAIRS(state, data) {
    state.fiatPairs = data;
  },
  SET_LOADING(state, value) {
    state.loading = value;
  },
  SET_INSTANT_ASSETS(state, data) {
    state.instant = { ...state.instant, ...data };
  },
  SET_AUTO_ASSETS(state, data) {
    state.auto = { ...state.auto, ...data };
  },
  SET_OFFERS(state, data) {
    state.offers = data;
  },
  SET_THUMBNAILS(state, data) {
    state.thumbnails = data;
  },
  SET_SHOW_FIAT_TRADE_STATEMENT_MODAL(state, value) {
    state.showFiatTradeStatementModal = value;
  }
};

const actions: ActionTree<TradeState, {}> = {
  async loadPairs({ commit }) {
    let pairs = await this.$api.market.getATMPairs();
    pairs = pairs
      .filter(pair => pair.state === "Trading")
      .map(pair => utils.trade.replaceUsdtMeta(pair));
    commit("SET_PAIRS", pairs);
  },
  async loadOffers({ commit }) {
    const offers = await this.$api.market.getC2COffers();
    commit("SET_OFFERS", offers);
  },
  async loadFiatPairs({ commit }) {
    const resp = await this.$api.market.getFiatPairs();
    const sort = ["BOX", "BTC", "ETH", "XIN", "EOS", "USDT"].reverse();
    const pairs = resp
      .sort((a, b) => (a.asset_symbol > b.asset_symbol ? -1 : 1))
      .sort((a, b) => {
        const ia = sort.indexOf(a.asset_symbol);
        const ib = sort.indexOf(b.asset_symbol);
        if (ia === ib) {
          return +a.price > +b.price ? -1 : 1;
        }
        return ia > ib ? -1 : 1;
      });
    commit("SET_FIAT_PAIRS", pairs);
  },
  async loadThumbnails({ commit }) {
    const thumbnails = await this.$api.market.getKlineThumbnail();
    commit("SET_THUMBNAILS", thumbnails);
  },
  async loadData({ dispatch, commit }) {
    commit("SET_LOADING", true);
    try {
      await Promise.all([
        dispatch("loadPairs"),
        // dispatch("loadOffers"),
        dispatch("loadFiatPairs"),
        dispatch("loadThumbnails")
      ]);
    } catch (error) {
      console.log(error);
    }
    commit("SET_LOADING", false);
  },
  removeTradeData({ dispatch }) {
    dispatch("trade/orders/removeFiatOrders", {}, { root: true });
    dispatch("trade/orders/removeAutoOrders", {}, { root: true });
    dispatch("trade/orders/removeInstantOrders", {}, { root: true });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
